import React from "react";
import PropTypes from "prop-types";

const ReferralStats = ({ referralData }) => {
  // Extract data from the new structure
  const summary = referralData?.summary || {};
  const statusBreakdown = referralData?.statusBreakdown || {};
  const rewardsSummary = referralData?.rewards?.summary || {};
  const referrals = referralData?.referrals || [];
  const usedReferralCode = referralData?.usedReferralCode || null;

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Used Referral Code Card - NEW ADDITION */}
      {usedReferralCode && (
        <div className="md:col-span-2 bg-white rounded-xl shadow-md overflow-hidden mb-4">
          <div className="bg-gradient-to-r from-purple-500 to-indigo-600 p-4">
            <h3 className="text-xl font-bold text-white">Used Referral Code</h3>
          </div>
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 bg-purple-50 rounded-lg gap-2">
                  <span className="text-gray-700 font-medium whitespace-nowrap">
                    Referral Code
                  </span>
                  <span className="font-bold text-purple-600 break-all">
                    {usedReferralCode.referralCode}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 bg-gray-50 rounded-lg gap-2">
                  <span className="text-gray-700 font-medium whitespace-nowrap">
                    Referred by
                  </span>
                  <div className="flex flex-col items-start sm:items-end w-full sm:w-auto">
                    <span className="font-semibold text-gray-800">
                      {usedReferralCode.referrerName}
                    </span>
                    <span className="text-xs text-gray-500 break-all">
                      {usedReferralCode.referrerEmail}
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 bg-gray-50 rounded-lg gap-2">
                  <span className="text-gray-700 font-medium whitespace-nowrap">
                    Applied Date
                  </span>
                  <span className="font-medium text-gray-600">
                    {new Date(
                      usedReferralCode.appliedDate
                    ).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-3 bg-gray-50 rounded-lg gap-2">
                  <span className="text-gray-700 font-medium">Status</span>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold ${usedReferralCode.status === "ACTIVE"
                      ? "bg-green-100 text-green-800"
                      : usedReferralCode.status === "PENDING"
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-gray-100 text-gray-800"
                      }`}
                  >
                    {usedReferralCode.status}
                  </span>
                </div>
              </div>
            </div>
            {usedReferralCode.source && (
              <div className="mt-4 p-3 bg-indigo-50 rounded-lg">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                  <span className="text-gray-700 font-medium whitespace-nowrap">Source</span>
                  <span className="font-medium text-indigo-600">
                    {usedReferralCode.source}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Referral Statistics Card */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-primary p-4">
          <h3 className="text-xl font-bold text-white">Referral Statistics</h3>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            {/* Referral code section */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <span className="text-gray-700 font-medium whitespace-nowrap">
                  Your Referral Code
                </span>
                <span className="font-bold text-primary break-all">
                  {summary.referralCode}
                </span>
              </div>
            </div>

            {/* Status breakdown */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-yellow-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zm0 16a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                  </svg>
                </div>
                <span className="text-gray-700 font-medium">
                  Pending Referrals
                </span>
              </div>
              <span className="text-2xl font-bold text-gray-800">
                {statusBreakdown.pending?.count || 0}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-blue-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <span className="text-gray-700 font-medium">
                  Qualified Referrals
                </span>
              </div>
              <span className="text-2xl font-bold text-gray-800">
                {statusBreakdown.qualified?.count || 0}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-green-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <span className="text-gray-700 font-medium">
                  Converted Referrals
                </span>
              </div>
              <span className="text-2xl font-bold text-gray-800">
                {statusBreakdown.converted?.count || 0}
              </span>
            </div>

            {/* Total investment row */}
            <div className="flex items-center justify-between bg-gray-50 p-4 py-4 rounded-lg">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-indigo-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                    <path
                      fillRule="evenodd"
                      d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <span className="text-gray-700 font-medium">
                  Total Investment
                </span>
              </div>
              <span className="text-2xl font-bold text-indigo-600">
                ₹
                {Object.values(statusBreakdown).reduce(
                  (sum, status) => sum + (status.totalInvestment || 0),
                  0
                )}
              </span>
            </div>

            {/* Total Referrals */}
            <div className="flex items-center justify-between bg-primary/10 p-4 rounded-lg">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-primary"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                </div>
                <span className="text-gray-700 font-medium">
                  Total Referrals
                </span>
              </div>
              <span className="text-2xl font-bold text-primary">
                {summary.totalReferrals}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Reward Statistics Card */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-primary p-4">
          <h3 className="text-xl font-bold text-white">Reward Statistics</h3>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-indigo-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm7-1a1 1 0 11-2 0 1 1 0 012 0zm-7.5 3.5a.5.5 0 01.5-.5h6a.5.5 0 010 1h-6a.5.5 0 01-.5-.5z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <span className="text-gray-700 font-medium">
                  Active Referral Value
                </span>
              </div>
              <span className={`text-2xl font-bold ${summary.activeRewardAmount > 0 ? "text-indigo-600" : "text-gray-400"}`}>
                {summary.activeRewardAmount > 0 ? `₹${summary.activeRewardAmount.toLocaleString("en-IN")}` : "Standard Phase"}
              </span>
            </div>

            {/* Program type info */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                <span className="text-gray-700 font-medium whitespace-nowrap">Program Type</span>
                <span className="font-bold text-primary break-all">
                  {summary.programType}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-orange-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <span className="text-gray-700 font-medium">
                  Pending Rewards
                </span>
              </div>
              <span className="text-2xl font-bold text-gray-800">
                ₹{rewardsSummary.pending?.amount || 0}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-green-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <span className="text-gray-700 font-medium">Paid Rewards</span>
              </div>
              <span className="text-2xl font-bold text-gray-800">
                ₹{rewardsSummary.paid?.amount || 0}
              </span>
            </div>

            {/* Cancelled rewards */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-red-500"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <span className="text-gray-700 font-medium">
                  Cancelled Rewards
                </span>
              </div>
              <span className="text-2xl font-bold text-gray-800">
                ₹{rewardsSummary.cancelled?.amount || 0}
              </span>
            </div>

            <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 text-primary"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                    <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
                  </svg>
                </div>
                <span className="text-gray-700 font-medium">Total Rewards</span>
              </div>
              <span className="text-2xl font-bold text-primary">
                ₹{summary.totalRewards}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Referrals Detail List */}
      <div className="md:col-span-2 bg-white rounded-xl shadow-lg overflow-hidden mb-16 mt-3 ">
        <div className="bg-gradient-to-r from-blue-500 to-primary p-4">
          <h3 className="text-xl font-bold text-white">Senior Referrals</h3>
        </div>
        <div className="overflow-x-auto">
          <div
            className="overflow-x-auto overflow-y-auto max-h-[60vh]"
            style={{
              scrollbarWidth: "auto",
              WebkitOverflowScrolling: "touch",
              overflowY: "scroll",
              scrollbarColor: "rgba(73, 78, 85, 0.52) transparent",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.scrollbarColor =
                "rgba(73, 78, 85, 0.52) transparent";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.scrollbarColor =
                "rgba(73, 78, 85, 0.52) transparent";
            }}
          >
            <table className="min-w-full divide-y divide-gray-200 ">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    KYC
                  </th>
                  {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Investment</th> */}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {referrals.length > 0 ? (
                  referrals.map((referral, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {referral.fullName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {referral.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${referral.status === "PENDING"
                              ? "bg-yellow-100 text-yellow-800"
                              : referral.status === "QUALIFIED"
                                ? "bg-green-100 text-green-800"
                                : referral.status === "CONVERTED"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-800"
                            }`}
                        >
                          {referral.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {referral.isKycVerified ? (
                          <span className="text-green-500 flex items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 mr-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Verified
                          </span>
                        ) : (
                          <span className="text-gray-500 flex items-center">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 mr-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Pending
                          </span>
                        )}
                      </td>
                      {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {referral.conversionData && referral.conversionData.firstInvestmentAmount ? 
                        `₹${referral.conversionData.firstInvestmentAmount}` : 
                        '—'}
                    </td> */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(referral.dateReferred).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-4 text-center text-sm text-gray-500"
                    >
                      No referrals found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

ReferralStats.propTypes = {
  referralData: PropTypes.shape({
    summary: PropTypes.shape({
      totalReferrals: PropTypes.number,
      totalRewards: PropTypes.number,
      referralCode: PropTypes.string,
      programType: PropTypes.string,
    }),
    statusBreakdown: PropTypes.object,
    rewards: PropTypes.shape({
      summary: PropTypes.object,
      typeBreakdown: PropTypes.object,
    }),
    referrals: PropTypes.array,
    usedReferralCode: PropTypes.shape({
      referralCode: PropTypes.string,
      appliedDate: PropTypes.string,
      status: PropTypes.string,
      referrerName: PropTypes.string,
      source: PropTypes.string,
    }),
  }).isRequired,
};

export default ReferralStats;
